/**
 * "Can this browser start an immersive session?" — the one WebXR question that can be asked
 * without loading WebXR.
 *
 * **Split out of `XrManager` for the bundle, not for tidiness.** `XrManager` imports Babylon's
 * engine, its glTF loader, and every WebXR feature module the session needs (teleportation,
 * pointer selection, controller models) — around six megabytes of the product bundle — and it has
 * to, because those imports are what make the features exist at all under tree-shaking
 * (CLAUDE.md, "Babylon features need explicit imports"). `XrManager.isSupported()` needs none of
 * it: it is four lines against `navigator.xr`, and it is the *first* thing the application asks,
 * on every load, from the router's own default-route resolution — before anybody has chosen to
 * enter a room and possibly instead of it.
 *
 * So the capability check living on the class meant that deciding *not* to show VR cost exactly
 * as much as showing it. This module is the same four lines with no imports at all;
 * `XrManager.isSupported` delegates here and stays where every existing caller and test already
 * expects to find it.
 */

/**
 * True when this browser can actually start an immersive session.
 *
 * Distinct from `'xr' in navigator`, which is the check this is easy to mistake for: a desktop
 * Chrome with no headset attached exposes the whole WebXR API and then refuses the session. The
 * `try` is not defensive padding either — `isSessionSupported` rejects rather than resolving
 * `false` in some browsers, and a rejection here would take out the router's default-route
 * resolution on a machine that simply has no headset.
 */
export async function isImmersiveVrSupported(): Promise<boolean> {
  if (!('xr' in navigator) || navigator.xr === undefined) {
    return false;
  }

  try {
    return (await navigator.xr.isSessionSupported('immersive-vr')) === true;
  } catch {
    return false;
  }
}
